/* Copyright (c) 2020-present CloudIO Inc */

import {
  checkForFixedChild,
  isAllowedInsideParentDuplicateCheck,
  toOptions,
} from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Popover';
import { helpRenders } from './Popover_Help';

const name = 'Popover';

const PopoverManager: ComponentManagerFn<'Popover'> = (
  platform: Platform,
): ComponentManager<'Popover'> => {
  return {
    ...baseComponentManager<'Popover'>(platform, 'Popover'),
    defaultValue,
    defaultChildren: ['FlexPanel'],
    isChildAllowed: (page, item, childType, context) =>
      (
        [
          'Box',
          'BoxContainer',
          'CanvasGrid',
          'Card',
          'Chart',
          'DataGrid',
          'DataStoreReference',
          'FlexPanel',
          'Form',
          'HorizontalSplitPanel',
          'RowPanel',
          'SearchForm',
          'TabPanel',
          'TriggerOnPopoverClose',
          'TriggerOnPopoverOpen',
          'VerticalSplitPanel',
        ] as ItemType[]
      ).includes(childType) &&
      checkForFixedChild(platform, page, item, childType, name, context),
    props: {
      popoverWidth: {
        label: 'Width',
        type: 'number',
        max: 800,
        min: 300,
        step: 40,
      },
      popoverHeight: {
        label: 'Height',
        type: 'number',
        max: 800,
        min: 300,
        step: 40,
      },
      open: {
        label: 'Open',
        type: 'boolean',
      },
      delay: {
        label: 'Delay (ms)',
        type: 'numberInput',
      },
      openCounter: {
        label: 'HIDDEN',
        type: 'numberInput',
      },
      openOnMouseEnter: {
        label: 'Open On Mouse Enter',
        type: 'boolean',
      },
      closeOnMouseLeave: {
        label: 'Close On Mouse Leave',
        type: 'boolean',
      },
      closeOnOutSideClick: {
        label: 'Close On Outside Click',
        type: 'boolean',
      },
      disableFlip: {
        label: 'Disable Flip',
        type: 'boolean',
      },
      placement: {
        label: 'Placement',
        type: 'select',
        options: toOptions([
          'auto',
          'auto-end',
          'auto-start',
          'bottom',
          'bottom-end',
          'bottom-start',
          'left',
          'left-end',
          'left-start',
          'right',
          'right-end',
          'right-start',
          'top',
          'top-end',
          'top-start',
        ]),
        helpText: 'Specify the placement for the popover to display.',
        required: true,
      },
      borderRadius: {
        label: 'Border Radius',
        type: 'number',
        max: 12,
      },
      backgroundColor: {
        label: 'BG Color',
        type: 'BGColor',
        allowCustomColor: true,
      },
    },
    isPropertyVisible: (property) => property !== 'openCounter',
    isAllowedInsideParent: (page, item, context) =>
      isAllowedInsideParentDuplicateCheck(
        platform,
        page,
        item,
        'Popover',
        context,
        'ALL',
      ),
    category: 'Containers',
    name,
    noOverlay: true,
    canHaveChildren: () => true,
    layout: 'fixed',
    icon: { iconName: 'window-restore' },
    description: `**Overview**<br/>
The Popover is a floating container designed to display rich, secondary content on top of the current page. Unlike a modal or drawer, a Popover is "anchored" to a specific UI element (like a button or icon). It provides users with contextual details—such as filter options, quick forms, or help text—without forcing them to navigate away or lose their place on the screen.

**1. Positioning & Alignment**<br/>
Because the Popover is context-dependent, controlling where it appears relative to its anchor is critical.
* **Placement:** Defines the preferred position of the popover. You can choose specific directions (e.g., top, bottom-start, right) or allow the system to decide (auto).
* **Disable Flip:** By default, if there isn't enough screen space in the preferred position (e.g., you set top but the element is at the very top of the screen), the popover automatically "flips" to the opposite side to stay visible. Enable this property to force the popover to stay in its defined position regardless of available space.

**2. Interaction & Triggers**<br/>
You can customize how the user invokes and dismisses the popover to match the workflow—whether it's a quick hover for info or a click for interaction.
* **Open:** A boolean flag to control visibility programmatically.
* **Open On Mouse Enter:** If enabled, the popover appears automatically when the user hovers over the anchor element.
* **Close On Mouse Leave:** If enabled, the popover closes automatically when the cursor moves away.
* **Close On Outside Click:** If enabled, clicking anywhere else on the screen will dismiss the popover. This is standard behavior for menus and settings panels.
* **Delay (ms):** Adds a time buffer before the popover opens. This is useful for preventing "flickering" if a user accidentally brushes past the trigger.

**3. Dimensions & Styling**<br/>
Unlike flexible page layouts, Popovers require defined constraints to ensure they don't obscure too much of the underlying content.
* **Width & Height:** Sets the fixed dimensions of the floating container. (Range: Min 300px, Max 800px).
* **Border Radius:** Controls the roundness of the corners (Max 12).
* **BG Color:** Sets the background color of the container.

**4. Content Composition**<br/>
The Popover is a robust container capable of hosting interactive elements.
* **Supported Children:** It is designed to hold complex layouts including Forms, SearchForms, Charts, DataGrids, and FlexPanels.
* **Triggers:** You can attach specific logic triggers (TriggerOnPopoverOpen, TriggerOnPopoverClose) to execute actions exactly when the user interacts with the component.`,
    helpRenders,
  };
};

export default PopoverManager;
